import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Save, Loader2 } from 'lucide-react';
import { useRecordBulkDamages } from '@/hooks/useInventory';
import { DAMAGE_TYPE_LABELS, type DamageType } from '@/types/database/inventory';
import { CATEGORY_TYPES, CATEGORY_TYPE_COLORS, type CategoryType } from '@/constants/categoryTypes';
import { DateNavigator } from './DateNavigator';

interface ProductWithCategory {
  id: string;
  name: string;
  category_id: string | null;
  category_name: string | null;
  category_type: CategoryType | null;
}

interface DamageEntry {
  quantity: string;
  damage_type: DamageType;
  reason: string;
}

interface BulkDamageEntryTableProps {
  onSaveSuccess?: () => void;
}

export function BulkDamageEntryTable({ onSaveSuccess }: BulkDamageEntryTableProps) {
  const [damageDate, setDamageDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [activeTab, setActiveTab] = useState<string>('All');
  const [entries, setEntries] = useState<Record<string, DamageEntry>>({});

  const recordBulkDamages = useRecordBulkDamages();

  // Fetch products with their categories
  const { data: products, isLoading } = useQuery({
    queryKey: ['products-with-categories'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('products')
        .select(`
          id,
          name,
          category_id,
          category:categories(name, category_type)
        `)
        .order('name');

      if (error) throw error;

      return (data || []).map((p: any) => ({
        id: p.id,
        name: p.name,
        category_id: p.category_id,
        category_name: p.category?.name || null,
        category_type: p.category?.category_type || null,
      })) as ProductWithCategory[];
    },
  });

  // Group products by category type
  const groupedProducts = useMemo(() => {
    if (!products) return { Fresh: [], FMCG: [], Other: [], All: [] };

    const grouped: Record<string, ProductWithCategory[]> = {
      Fresh: [],
      FMCG: [],
      Other: [],
      All: [...products],
    };

    products.forEach((product) => {
      const type = product.category_type || 'Other';
      if (grouped[type]) {
        grouped[type].push(product);
      } else {
        grouped.Other.push(product);
      }
    });

    // Sort All by category type then name
    grouped.All.sort((a, b) => {
      const typeOrder = { Fresh: 0, FMCG: 1, Other: 2 };
      const aOrder = typeOrder[a.category_type as keyof typeof typeOrder] ?? 2;
      const bOrder = typeOrder[b.category_type as keyof typeof typeOrder] ?? 2;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return a.name.localeCompare(b.name);
    });

    return grouped;
  }, [products]);

  // Get products for current tab
  const displayProducts = groupedProducts[activeTab] || [];

  // Count entries with quantities
  const pendingCount = useMemo(() => {
    return Object.values(entries).filter(
      (e) => e.quantity && parseFloat(e.quantity) > 0
    ).length;
  }, [entries]);

  // Update entry for a product
  const updateEntry = (productId: string, field: keyof DamageEntry, value: string) => {
    setEntries((prev) => ({
      ...prev,
      [productId]: {
        quantity: prev[productId]?.quantity || '',
        damage_type: prev[productId]?.damage_type || 'spoiled',
        reason: prev[productId]?.reason || '',
        [field]: value,
      },
    }));
  };

  // Handle bulk save
  const handleSave = async () => {
    const damagesToRecord = Object.entries(entries)
      .filter(([, entry]) => entry.quantity && parseFloat(entry.quantity) > 0)
      .map(([productId, entry]) => ({
        product_id: productId,
        damage_date: damageDate,
        quantity: parseFloat(entry.quantity),
        damage_type: entry.damage_type,
        reason: entry.reason || undefined,
      }));

    if (damagesToRecord.length === 0) return;

    await recordBulkDamages.mutateAsync(damagesToRecord);

    // Clear entries after successful save
    setEntries({});
    
    // Notify parent of success
    onSaveSuccess?.();
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with date and save button */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="space-y-1">
            <Label>Date</Label>
            <DateNavigator date={damageDate} onChange={setDamageDate} />
          </div>
        </div>

        <Button
          onClick={handleSave}
          disabled={pendingCount === 0 || recordBulkDamages.isPending}
          className="gap-2"
        >
          {recordBulkDamages.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          {pendingCount > 0 ? `Save ${pendingCount} Damages` : 'Save Damages'}
        </Button>
      </div>

      {/* Category tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          {CATEGORY_TYPES.map((type) => (
            <TabsTrigger key={type} value={type} className="gap-2">
              <span
                className={`h-2 w-2 rounded-full ${
                  type === 'Fresh'
                    ? 'bg-green-500'
                    : type === 'FMCG'
                    ? 'bg-blue-500'
                    : 'bg-gray-500'
                }`}
              />
              {type}
              <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                {groupedProducts[type]?.length || 0}
              </Badge>
            </TabsTrigger>
          ))}
          <TabsTrigger value="All" className="gap-2">
            All
            <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
              {products?.length || 0}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* Table content for each tab */}
        {[...CATEGORY_TYPES, 'All'].map((tabValue) => (
          <TabsContent key={tabValue} value={tabValue} className="mt-4">
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[250px]">Product</TableHead>
                    {tabValue === 'All' && <TableHead className="w-[100px]">Category</TableHead>}
                    <TableHead className="w-[100px]">Quantity</TableHead>
                    <TableHead className="w-[140px]">Type</TableHead>
                    <TableHead>Reason</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {displayProducts.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={tabValue === 'All' ? 5 : 4}
                        className="py-8 text-center text-muted-foreground"
                      >
                        No products in this category
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayProducts.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        {tabValue === 'All' && (
                          <TableCell>
                            {product.category_type && (
                              <Badge
                                variant="outline"
                                className={CATEGORY_TYPE_COLORS[product.category_type as CategoryType]}
                              >
                                {product.category_type}
                              </Badge>
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0"
                            value={entries[product.id]?.quantity || ''}
                            onChange={(e) => updateEntry(product.id, 'quantity', e.target.value)}
                            className="w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Select
                            value={entries[product.id]?.damage_type || 'spoiled'}
                            onValueChange={(v) => updateEntry(product.id, 'damage_type', v)}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(DAMAGE_TYPE_LABELS).map(([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="Optional reason..."
                            value={entries[product.id]?.reason || ''}
                            onChange={(e) => updateEntry(product.id, 'reason', e.target.value)}
                            className="w-full"
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Pending save indicator */}
      {pendingCount > 0 && (
        <div className="text-sm text-muted-foreground">
          {pendingCount} product{pendingCount !== 1 ? 's' : ''} with quantities will be saved
        </div>
      )}
    </div>
  );
}
